import * as ngCore from '@angular/core'
import * as rxjsInterop from '@angular/core/rxjs-interop'
import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as select from '../select'

import { Language } from './language'
import { MonacoManagerService } from './monaco-manager.service'
import { Syntax } from './syntax'

const CONTAINER_EXISTENCE_POLLING_FREQ_MS = 100

const EMPTY_VALUE = ''

/**
 * Used by {@link Component._executeEmitter}
 *
 * __NOTE 1:__ we limit customizations here because we want to have a standard
 * experience with our code editors, rather than each instance of the code
 * editor being a little different
 *
 * __NOTE 2:__ this must be a callback because some of the properties use
 * {@link monaco} which is not initialized at first
 */
const EXECUTE_ACTION: () => Omit<
  monaco.editor.IActionDescriptor,
  'run'
> = () => ({
  // NOTE: the prefix affects position in the menu as the menu items are sorted
  // alphabetically (see description of this property for more details)
  contextMenuGroupId: '91_execution',
  contextMenuOrder: 0,
  id: '@attain-apps.execute',
  keybindings: [
    // eslint-disable-next-line no-bitwise
    monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyE,
  ],
  label: 'Execute',
})

/**
 * {@link https://github.com/microsoft/monaco-editor Monaco} code editor
 *
 * #### Inputs
 * - {@link Component.valueSg|value}?
 * - {@link Component.labelSg|label}?
 * - {@link Component.languageSg|language}?
 * - {@link Component.disabledSg|disabled}?
 * - {@link Component.loadingSg|loading}?
 * - {@link Component.readonlySg|readonly}?
 * - {@link Component.schema|readonly}
 *
 * #### Outputs
 * - {@link Component.valueChange|valueChange}
 *
 * @example
 * ```html
 * <atn-ds-code
 *   [label]="'My Code'"
 *   [value]="code"
 *   [syntax]="'json'"
 *   [disabled]="false"
 *   [readonly]="false"
 *   [loading]="false"
 *   [schema]="schema"
 *   (valueChange)="handleChange($event)"
 * ></atn-ds-code>
 * ```
 */
@ngCore.Component({
  imports: [select.single.Component],
  selector: 'atn-ds-code',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component implements ngCore.AfterViewInit {
  /**
   * Code content
   *
   * This is set-up for two-way binding to emit updates on changes
   */
  public readonly valueSg = ngCore.model<string | undefined>(EMPTY_VALUE, {
    alias: 'value',
  })
  /** Applies {@link valueSg} to the Monaco editor */
  public readonly applyValue = ngCore.effect(() => {
    const value = this.valueSg() ?? EMPTY_VALUE
    this._monacoManager.do(({ model }) => {
      if (!this._isSame(model.getValue(), value)) {
        model.setValue(value)
      }
    })
  })

  /**
   * JSON value to be displayed in a JSON code editor
   *
   * This is set-up for two-way binding to emit updates on changes
   *
   * __NOTE:__ this should be used instead of, not in addition to,
   * {@link valueSg}
   */
  public readonly jsonSg = ngCore.model<unknown>(undefined, {
    alias: 'json',
  })
  /**
   * Used by {@link _onJsonChange} and {@link _onValueChange}
   */
  private _jsonEffectInitialized = false
  /** Writes {@link jsonSg} to {@link valueSg} */
  private readonly _onJsonChange = ngCore.effect(() => {
    const json = this.jsonSg()
    const jsonEffectInitialized = this._jsonEffectInitialized
    this._jsonEffectInitialized = true
    if (json === undefined && !jsonEffectInitialized) {
      // Avoids setting value to empty when the effect is first ran
      return
    }
    if (json !== undefined) {
      // If we haven't picked a syntax/language, we can assume JSON is the way to
      // go
      this._monacoManager.syntax ??= Syntax.of('json')
    }
    const value = ngCore.untracked(this.valueSg)
    let oldJson = json
    if (value === EMPTY_VALUE || value === undefined) {
      oldJson = undefined
    } else {
      try {
        oldJson = JSON.parse(value)
      } catch (err) {}
    }
    if (!isDeepEqual(json, oldJson)) {
      // We only change the value if it does not match the new json
      this.valueSg.set(
        json === undefined ? EMPTY_VALUE : JSON.stringify(json, null, 2),
      )
    }
  })
  /** Writes {@link valueSg} to {@link jsonSg} */
  private readonly _onValueChange = ngCore.effect(() => {
    const value = this.valueSg()
    const json = ngCore.untracked(this.jsonSg)
    const jsonEffectInitialized = this._jsonEffectInitialized
    this._jsonEffectInitialized = true
    if (value === EMPTY_VALUE && !jsonEffectInitialized) {
      // Avoids setting JSON to undefined when the effect is first ran
      return
    }
    let newJson = json
    if (value === EMPTY_VALUE || value === undefined) {
      newJson = undefined
    } else {
      try {
        newJson = JSON.parse(value)
      } catch (err) {}
    }
    if (!isDeepEqual(json, newJson)) {
      // We only change the json if it does not match the new value
      this.jsonSg.set(newJson)
    }
  })

  /**
   * Label above the code editor
   */
  public readonly labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })

  /** Whether there's an ability to select syntax */
  public readonly syntaxSelectorSg = ngCore.input<boolean>(false, {
    alias: 'syntaxSelector',
  })

  /** TODO:  */
  public readonly availableSyntaxesSg = ngCore.computed(() =>
    this.syntaxSelectorSg()
      ? ['dms-query', 'json', 'text'].map(
          id => ({ label: id, value: id }) satisfies select.models.Item,
        )
      : [],
  )

  /**
   * Identifies the {@link Syntax} of the code
   *
   * Passed as an argument to {@link Syntax.of}
   */
  public readonly syntaxIdSg = ngCore.model<string | undefined>(undefined, {
    alias: 'syntax',
  })
  /** Applies {@link syntaxIdSg} to the Monaco editor */
  public readonly applySyntax = ngCore.effect(() => {
    const syntaxId = this.syntaxIdSg()
    this._monacoManager.syntax = Syntax.of(syntaxId)
  })

  /**
   * Whether the input is loading
   */
  public readonly loadingSg = ngCore.input<boolean | undefined>(false, {
    alias: 'loading',
  })

  /**
   * Whether the input is disabled
   */
  public readonly disabledSg = ngCore.input<boolean | undefined>(false, {
    alias: 'disabled',
  })

  /**
   * Whether the input is readonly
   *
   * __NOTE:__ In case we are dealing with JSON based syntax, setting this to true will prevent diagnostics on JSON code.
   */
  public readonly readonlySg = ngCore.input<boolean | undefined>(false, {
    alias: 'readonly',
  })
  /** Applies {@link readonlySg} to the Monaco editor */
  public readonly applyReadonly = ngCore.effect(() => {
    const readonly = this.readonlySg()
    if (readonly) {
      this._monacoManager.do(({ editor }) => {
        editor.updateOptions({ readOnly: true })
      })
    }
  })

  /**
   * Input signal for {@link ents.core.dms.models.Schema}. Required for setting up suggestion feature for the DmsQl.
   */
  public readonly schemaSg = ngCore.input<
    ents.core.dms.models.Schema | undefined
  >(undefined, {
    alias: 'schema',
  })
  /** Applies {@link schemaSg} to the Monaco editor to enable DMS QL completion provider */
  public readonly applySchemaOnEditor = ngCore.effect(() => {
    this._monacoManager.dmsSchema = this.schemaSg()
  })

  /**
   * {@link rxjs.Observable} powering {@link execute}
   *
   * Used to ensure we only add an action to the editor if it has associated
   * logic to run
   *
   * Ideally, we would be able to do this with a standard {@link ngCore.output},
   * but currently (2/22/2025) we cannot because of this issue:
   * {@link https://github.com/angular/angular/issues/54837}
   */
  private readonly _executeEmitter = rxjs.defer(() => {
    const subject = new rxjs.Subject<void>()
    // We only add this action if this output is being used
    this._monacoManager.do(({ editor }) => {
      editor.addAction({
        ...EXECUTE_ACTION(),
        run: () => subject.next(),
      })
    })
    return subject
  })
  /**
   * Ran whenever the user chooses to use the `Execute` action in the editor
   */
  public readonly execute = rxjsInterop.outputFromObservable(
    this._executeEmitter,
  )

  /**
   * Monaco code editor is dynamically added to this
   */
  @ngCore.ViewChild('container')
  private readonly _container?: ngCore.ElementRef<HTMLElement | undefined>

  public get canBeFullscreen() {
    return document.fullscreenEnabled
  }
  public get isFullscreen() {
    return document.fullscreenElement === this._container?.nativeElement
  }

  /** @see {@link MonacoManagerService} */
  private readonly _monacoManager = ngCore
    .inject(MonacoManagerService)
    .createManager()

  constructor() {
    this._monacoManager.do(({ editor }) => {
      // Used to emit outputs
      editor.onDidChangeModelContent(() => {
        const value = editor?.getValue() ?? EMPTY_VALUE
        const inputtedValue = this.valueSg() ?? EMPTY_VALUE
        if (value !== inputtedValue) {
          // Only outputs if the value has changed
          this.valueSg.set(value)
        }
      })
    })
  }

  /** TODO:  */
  public onSyntaxChangeHandler(event: unknown): void {
    // Handle syntax change
    this.syntaxIdSg.set(
      // Since dropdown returns an 'unknown' type, we need to assert it
      // to a string
      TypeAsserter.validate(event, TypeAsserter.assertIsString),
    )
  }

  /**
   * Primarily, calls {@link _createMonacoEditor}
   */
  public ngAfterViewInit(): void {
    const containerElem = this._container?.nativeElement
    if (containerElem) {
      // If the container element already exists, we can create the editor
      this._monacoManager.bindTo(containerElem)
    } else {
      // Otherwise, we need to poll periodically until the container exists
      const interval = setInterval(() => {
        const _containerElem = this._container?.nativeElement
        if (_containerElem) {
          this._monacoManager.bindTo(_containerElem)
          clearInterval(interval)
        }
      }, CONTAINER_EXISTENCE_POLLING_FREQ_MS)
    }
  }

  /**
   * Expands the code editor to fullscreen, or collapses it out of fullscreen
   */
  public async toggleFullscreen(): Promise<void> {
    if (this.isFullscreen) {
      await document.exitFullscreen()
    } else {
      await this._container?.nativeElement?.requestFullscreen()
    }
  }

  /**
   * In the setter for {@link valueSg}, we check if the values have changed
   * before updating them because updating them can cause the cursor to reset
   * which can be annoying for a user. Instead of just checking for straight
   * equality, this function more generally checks if the content has
   * effectively changed, especially for JSON code
   *
   * @example
   * _isSame('{ "a": 1 }', '{"a":1}') // true
   * _isSame('{ "a": 1 }', '{"a":2}') // false
   */
  private _isSame(value1: string, value2: string): boolean {
    if (value1 === value2) {
      return true
    }
    // if (this.monacoModel.getLanguageId() !== 'json') {
    //   return false
    // }
    try {
      return _standardJson(value1) === _standardJson(value2)
    } catch (err) {
      return false
    }
  }
}

/**
 * Standardizes the format for a given JSON string to be used in comparisons
 *
 * Used by {@link _isSame}
 */
function _standardJson(json: string): string {
  // We don't make this human-readable because we just want the comparison to be
  // as efficient as possible
  return JSON.stringify(JSON.parse(json))
}
